import { createSlice } from '@reduxjs/toolkit';
import { parseRoute } from '../utils/common';
import { TIMEZONE_KEY_IN_LS } from '../constants';

const initialState = {
  selectedMenu: '',
  selectedSubMenu: '',
  selectedMenuItem: '',
  svgVersion: null,
  destinations: null,
  user: null,
  filterData: null,
  currency: '',
  pagination: null,
  activeModal: '',
  roles: null,
  permissions: null,
  roleCreateCallback: false,
  isLoading: true,
  isTennisLoading: true,
  isTennisEnabled: true,
  isFetching: {},
  toggleMenu: false,
  toggleSubMenu: false,
  errorMessage: null,
  modalData: null,
  cnt: null,
  // @TODO docs
  profileInfo: null,
  leagues: [],
  tennisCompetitions: [],
  tennisEventStatus: [],
  tennisMatchTimelineEvents: [],
  tennisOddsHistoryTemplate: [],
  markets: [],
  tennisMarkets: [],
  matchTimeline_events: [],
  oddsHistoryTemplate: [],
  periodId: [],
  eventType: [],
  heading: '',
  timezone: '',
  requiresSaveBeforeNavigation: false,
  nextNavigationLink: null,
  accounts: [],
};

const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    aLoginError: (state, action) => {
      state.loginError = action.payload.loginError;
      state.isLoading = false;
    },
    aLogoutSuccess: (state) => {
      state.user = null;
      state.profileInfo = null;
      state.loginError = '';
    },
    aSetIsLoading: (state, action) => {
      state.isLoading = action.payload.isLoading;
    },
    aSetIsTennisLoading: (state, action) => {
      state.isTennisLoading = action.payload.isLoading;
    },
    aSetSelectedMenu: (state, action) => {
      state.selectedMenu = action.payload.menu;
    },
    aSetSelectedSubMenu: (state, action) => {
      state.selectedSubMenu = action.payload.subMenuName || '';
    },
    aSetOpenModal: (state, action) => {
      const isFetching = state.isFetching;
      if (action.payload.key && action.payload.value === false) {
        delete isFetching[action.payload.key];
      }
      state.activeModal = action.payload.modal;
      state.modalData = action.payload.modalData ? action.payload.modalData : null;
      state.errorMessage = action.payload.errorMessage;
      state.isLoading = false;
      state.isFetching = action.payload.key ? isFetching : state.isFetching;
    },
    aLoginSuccess: (state, action) => {
      state.user = action.payload.user;
      state.sessionId = action.payload.sessionId;
    },
    aGetInitSuccess: (state, action) => {
      state.user = action.payload.user;
      state.sessionId = action.payload.sessionId;
      state.svgVersion = action.payload.svgVersion;
      state.currency = action.payload.currency;
      state.cnt = action.payload.cnt;
      state.isLoading = false;
      state.loginError = '';
      state.markets = action.payload.markets;
      state.matchTimelineEvents = action.payload.matchTimelineEvents;
      state.periods = action.payload.periods;
      state.events = action.payload.events;
      state.oddsHistoryTemplate = action.payload.oddsHistoryTemplate;
      state.leagues = action.payload.leagues;
      state.selectedMenu = action.payload.menu || '';
      state.selectedSubMenu = action.payload.selectedSubMenu || '';
      state.selectedMenuItem = action.payload.selectedMenuItem || '';
      state.accounts = action.payload.accounts;
      state.heading = action.payload.heading || '';
      state.filterData = action.payload.filters
        ? action.payload.filters
        : null;
    },
    aGetInitTenisSuccess: (state, action) => {
      state.tennisCompetitions = action.payload.competitions;
      state.tennisEventStatus = action.payload.eventStatus;
      state.tennisMatchTimelineEvents = action.payload.matchTimelineEvents;
      state.tennisOddsHistoryTemplate = action.payload.oddsHistoryTemplate;
      state.tennisMarkets = action.payload.markets;
      state.isTennisEnabled = action.payload.isTennisEnabled;
    },
    aGetInitTennisError: (state, action) => {
      state.isTennisEnabled = action.payload.isTennisEnabled;
    },
    aSetFilters: (state, action) => {
      state.filterData = action.payload.filter
        ? action.payload.filter
        : null;
      state.pagination = null;
    },
    aSetUserList: (state, action) => {
      state.pagination = action.payload
        ? action.payload.meta
        : null;
    },
    aRolesListSuccess: (state, action) => {
      state.pagination = action.payload
        ? action.payload.meta
        : null;
    },
    aSetPaginationData: (state, action) => {
      const pagination = state.pagination;
      pagination.page = action.payload.page;
      state.pagination = pagination;
    },
    aSetIsFetching: (state, action) => {
      const isFetching = state.isFetching;
      if (action.payload.value === true) {
        isFetching[action.payload.key] = action.payload.value;
      } else {
        delete isFetching[action.payload.key];
      }
      state.isFetching = isFetching;
    },
    aGetProfileInfoSuccess: (state, action) => {
      state.profileInfo = action.payload.profileInfo;
    },
    aAddIntervalSuccess: (state) => {
      state.activeModal = '';
      state.modalData = null;
    },
    aSetTimezone: (state, action) => {
      state[TIMEZONE_KEY_IN_LS] = action.payload.timezone;
    },
    aSetNavigationInfo: (state, action) => {
      const saveBeforeNavigation = action.payload?.requiresSaveBeforeNavigation !== undefined
        ? action.payload.requiresSaveBeforeNavigation
        : state.requiresSaveBeforeNavigation;
      state.requiresSaveBeforeNavigation = saveBeforeNavigation;
      state.nextNavigationLink = action.payload?.nextNavigationLink || null;
    },
    aLocationChanged: (state, action) => {
      const {
        selectedMenu,
        selectedSubMenu,
        selectedMenuItem,
        heading,
      } = parseRoute(action.payload);
      state.selectedMenu = selectedMenu || '';
      state.selectedSubMenu = selectedSubMenu || '';
      state.pagination = state.selectedMenuItem !== selectedMenuItem
        ? null
        : state.pagination;
      state.filterData = state.selectedMenuItem !== selectedMenuItem
        ? null
        : state.filterData;
      state.selectedMenuItem = selectedMenuItem || '';
      state.heading = heading || '';
      state.activeModal = null;
      state.toggleMenu = !state.toggleMenu;
      state.toggleSubMenu = !state.toggleSubMenu;
    },
  },
});

export const {
  aLoginError,
  aLogoutSuccess,
  aSetIsLoading,
  aSetIsTennisLoading,
  aSetSelectedMenu,
  aSetSelectedSubMenu,
  aSetOpenModal,
  aLoginSuccess,
  aGetInitSuccess,
  aGetInitTenisSuccess,
  aGetInitTennisError,
  aSetFilters,
  aSetUserList,
  aRolesListSuccess,
  aSetPaginationData,
  aSetIsFetching,
  aGetProfileInfoSuccess,
  aAddIntervalSuccess,
  aSetTimezone,
  aSetNavigationInfo,
  aLocationChanged,
} = commonSlice.actions;

export default commonSlice.reducer;
